import React, { useCallback, useEffect, useRef, useState } from 'react';
import Tree, { CustomNodeElementProps, RawNodeDatum, TreeNodeDatum } from 'react-d3-tree';
import type { HierarchyPointNode } from 'd3-hierarchy';
import { Box, Dialog, IconButton, SxProps, Theme, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  FitScreen as FitScreenIcon,
  Fullscreen as FullscreenIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { User, UserRole } from '../graphql/codegen/graphql-react';
import { dedupeUsersById, getHierarchyRootUsers, getRoleLabel } from '../helpers/userOrgHierarchy';
import { Body1 } from './ui/Typography';
import Flex from './ui/Flex';
import { colors } from '../theme';
import { ls } from '../i18n/translations';
import { useDarkMode } from '../hook/useDarkMode';
import { useAuth } from '../hook/useAuth';
import { PageName } from '../routes';

interface UserOrgChartProps {
  users: User[];
  /** Page context user: chart is rooted on this user (subtree); gets the highlight ring. Same as logged user on Profile; on Edit user page, the opened user. */
  currentUserId?: string | null;
  sx?: SxProps<Theme>;
}

/** Same fill for every node that shares the same role (enum value). */
const getFillColorForRole = (roleKey: string): string => {
  switch (roleKey) {
    case UserRole.Admin:
      return colors.blue;
    case UserRole.Admin2:
      return '#6d4c91';
    case UserRole.Supertechnician:
      return colors.orange;
    case UserRole.Technician:
      return '#156b3d';
    case UserRole.Viewer:
      return '#4a5d6e';
    default:
      return colors.blue;
  }
};

/** Truncates a string to maxLen characters, appending "…" if needed. */
const truncate = (str: string, maxLen: number): string =>
  str.length > maxLen ? `${str.slice(0, maxLen - 1)}…` : str;

/** Finds the depth/level of a node with given userId in the tree */
const findNodeDepth = (node: RawNodeDatum, userId: string, depth: number = 0): number | null => {
  if (node.attributes?.userId === userId) {
    return depth;
  }
  if (node.children) {
    for (const child of node.children) {
      const found = findNodeDepth(child, userId, depth + 1);
      if (found !== null) return found;
    }
  }
  return null;
};

/** Finds the position (depth and horizontal index) of a node in the tree */
const findNodePosition = (
  node: RawNodeDatum,
  userId: string,
  depth: number = 0,
  siblingIndex: number = 0,
  totalAtLevel: number = 1,
): { depth: number; siblingIndex: number; totalSiblings: number } | null => {
  if (node.attributes?.userId === userId) {
    return { depth, siblingIndex, totalSiblings: totalAtLevel };
  }
  if (node.children) {
    const totalChildren = node.children.length;
    for (let i = 0; i < totalChildren; i++) {
      const found = findNodePosition(node.children[i], userId, depth + 1, i, totalChildren);
      if (found !== null) return found;
    }
  }
  return null;
};

/**
 * Context user `currentUserId` is both the subtree root (selected user: profile = logged user;
 * edit-user = user opened in the page) and the highlighted node in the chart.
 */
const buildOrgTree = (
  users: User[],
  currentUserId?: string | null,
): { tree: RawNodeDatum | null; connectedUserIds: Set<string> } => {
  if (!users || users.length === 0) return { tree: null, connectedUserIds: new Set() };

  const usersDeduped = dedupeUsersById(users);
  if (usersDeduped.length === 0) return { tree: null, connectedUserIds: new Set() };

  const connectedUserIds = new Set<string>();

  /** Finds all ancestors of currentUser up to root */
  const findAncestors = (userId: string, visited = new Set<string>()): void => {
    if (visited.has(userId)) return;
    visited.add(userId);
    connectedUserIds.add(userId);
    const parent = usersDeduped.find((u) => u.id === userId)?.createdById;
    if (parent) findAncestors(parent, visited);
  };

  /** Finds all descendants of currentUser */
  const findDescendants = (userId: string, visited = new Set<string>()): void => {
    if (visited.has(userId)) return;
    visited.add(userId);
    connectedUserIds.add(userId);
    usersDeduped
      .filter((u) => u.createdById === userId && u.id !== userId)
      .forEach((child) => findDescendants(child.id!, visited));
  };

  if (currentUserId) {
    findAncestors(currentUserId);
    findDescendants(currentUserId);
  }

  /** Prevents infinite recursion on createdById cycles (A→B→A). */
  const buildNode = (user: User, ancestorIds: Set<string>): RawNodeDatum => {
    const id = user.id || '';
    const nextAncestors = new Set(ancestorIds);
    if (id) nextAncestors.add(id);

    const childUsers = usersDeduped.filter((u) => {
      if (!u.id || u.createdById !== user.id) return false;
      if (u.id === user.id) return false;
      if (ancestorIds.has(u.id)) return false;
      return true;
    });

    return {
      name: truncate(`${user.name || ''} ${user.surname || ''}`.trim() || user.email || '—', 22),
      attributes: {
        kind: 'user',
        userId: user.id ?? '',
        role: getRoleLabel(user.roles, user.roleId),
        roleKey: user.roles?.[0] ?? '',
        email: user.email || '',
        isCurrentUser: user.id === currentUserId,
        isConnectedToCurrentUser: connectedUserIds.has(user.id ?? ''),
      },
      children: childUsers.map((c) => buildNode(c, nextAncestors)),
    };
  };

  const viewerAsRoot =
    currentUserId != null && currentUserId !== ''
      ? usersDeduped.find((u) => u.id === currentUserId)
      : undefined;

  if (viewerAsRoot) {
    return { tree: buildNode(viewerAsRoot, new Set()), connectedUserIds };
  }

  const allRoots = getHierarchyRootUsers(usersDeduped);

  const virtualWrapper = (): RawNodeDatum => ({
    name: ls.users,
    attributes: {
      kind: 'virtual',
      userId: '',
      role: '',
      roleKey: '',
      email: '',
      isCurrentUser: false,
      isConnectedToCurrentUser: false,
    },
    children: allRoots.map((r) => buildNode(r, new Set())),
  });

  if (allRoots.length === 0) {
    return { tree: null, connectedUserIds };
  }

  if (allRoots.length === 1) {
    return { tree: buildNode(allRoots[0], new Set()), connectedUserIds };
  }

  return { tree: virtualWrapper(), connectedUserIds };
};

const NODE_W = 190;
const NODE_H = 64;
/** HTML inside foreignObject — not affected by MUI `color` on SVG text (fixes black-on-blue). Same stack as app theme. */
const ORG_CHART_FONT_FAMILY = '"Roboto Condensed", "Roboto", Arial, sans-serif';

/** Red ring for the user being edited — high contrast on any role color. */
const CURRENT_USER_RING = colors.red;

/** Initial zoom: lower = more of the tree visible (user can zoom in with wheel). */
const INITIAL_TREE_ZOOM = 0.3;

/** Zoom level when centering on current user — higher = more detail. */
const CENTER_ON_USER_ZOOM = 1.0;

const UserOrgChart: React.FC<UserOrgChartProps> = ({ users, currentUserId, sx }) => {
  const { isDarkMode } = useDarkMode();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const fullscreenContainerRef = useRef<HTMLDivElement>(null);
  const treeRef = useRef<any>(null);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(INITIAL_TREE_ZOOM);
  const [viewResetKey, setViewResetKey] = useState(0);
  const [ready, setReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fullscreenTranslate, setFullscreenTranslate] = useState({ x: 0, y: 0 });
  const [fullscreenZoom, setFullscreenZoom] = useState(0.15);
  const [fullscreenResetKey, setFullscreenResetKey] = useState(0);

  const { tree: treeData, connectedUserIds } = buildOrgTree(users, currentUserId);

  const applyInitialView = useCallback(() => {
    if (containerRef.current) {
      const { width } = containerRef.current.getBoundingClientRect();
      setTranslate({ x: width / 2, y: 72 });
      setZoom(INITIAL_TREE_ZOOM);
    }
  }, []);

  const applyCenterOnCurrentUser = useCallback(() => {
    if (!containerRef.current || !currentUserId || !treeData) return;

    const { width, height } = containerRef.current.getBoundingClientRect();
    const position = findNodePosition(treeData, currentUserId);

    if (position !== null) {
      const yOffset = position.depth * (NODE_H + 90);

      setTranslate({
        x: width / 2,
        y: height / 2 - yOffset * CENTER_ON_USER_ZOOM,
      });
      setZoom(CENTER_ON_USER_ZOOM);
    } else {
      setTranslate({ x: width / 2, y: height / 2 });
      setZoom(CENTER_ON_USER_ZOOM);
    }
  }, [currentUserId, treeData]);

  useEffect(() => {
    if (containerRef.current && treeData) {
      if (currentUserId) {
        // Wait for tree to render before centering
        setTimeout(() => {
          const nodeElement = document.querySelector(`[data-user-id="${currentUserId}"]`);
          if (nodeElement && containerRef.current) {
            const parentG = nodeElement.parentElement;
            if (parentG) {
              const { width, height } = containerRef.current.getBoundingClientRect();
              const transformAttr = parentG.getAttribute('transform');
              if (transformAttr) {
                const match = transformAttr.match(/translate\(([^,]+),([^)]+)\)/);
                if (match) {
                  const nodeX = parseFloat(match[1]);
                  const nodeY = parseFloat(match[2]);

                  setTranslate({
                    x: width / 2 - nodeX * CENTER_ON_USER_ZOOM,
                    y: height / 2 - nodeY * CENTER_ON_USER_ZOOM,
                  });
                  setZoom(CENTER_ON_USER_ZOOM);
                }
              }
            }
          } else {
            // Fallback
            applyCenterOnCurrentUser();
          }
        }, 200);
      } else {
        applyInitialView();
      }
      setReady(true);
    }
  }, [applyInitialView, applyCenterOnCurrentUser, currentUserId, treeData]);

  const handleCenterOnCurrentUser = useCallback(() => {
    if (!containerRef.current || !currentUserId || !treeData) return;

    // Try to find the node in the DOM
    setTimeout(() => {
      const nodeElement = document.querySelector(`[data-user-id="${currentUserId}"]`);
      if (nodeElement && containerRef.current) {
        const parentG = nodeElement.parentElement;
        if (parentG) {
          const { width, height } = containerRef.current.getBoundingClientRect();

          // Get the transform attribute to find the actual coordinates
          const transformAttr = parentG.getAttribute('transform');
          if (transformAttr) {
            const match = transformAttr.match(/translate\(([^,]+),([^)]+)\)/);
            if (match) {
              const nodeX = parseFloat(match[1]);
              const nodeY = parseFloat(match[2]);

              setTranslate({
                x: width / 2 - nodeX * CENTER_ON_USER_ZOOM,
                y: height / 2 - nodeY * CENTER_ON_USER_ZOOM,
              });
              setZoom(CENTER_ON_USER_ZOOM);

              setTimeout(() => {
                setViewResetKey((k) => k + 1);
              }, 50);
            }
          }
        }
      }
    }, 100);
  }, [currentUserId, treeData]);

  const handleShowAll = useCallback(() => {
    setIsFullscreen(true);
  }, []);

  const handleCloseFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const handleResetFullscreenView = useCallback(() => {
    setFullscreenResetKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (isFullscreen && fullscreenContainerRef.current && treeData) {
      const { width, height } = fullscreenContainerRef.current.getBoundingClientRect();
      
      // Always center on root for fullscreen view
      setFullscreenTranslate({ x: width / 2, y: 100 });
      setFullscreenZoom(0.5);
    }
  }, [isFullscreen, treeData, fullscreenResetKey]);

  const pathClassFunc = useCallback(
    (linkDatum: any) => {
      const sourceUserId = linkDatum.source?.data?.attributes?.userId;
      const targetUserId = linkDatum.target?.data?.attributes?.userId;
      const isConnected =
        (sourceUserId && connectedUserIds.has(sourceUserId)) ||
        (targetUserId && connectedUserIds.has(targetUserId));
      return isConnected ? 'link-connected-to-current-user' : '';
    },
    [connectedUserIds],
  );

  const handleNodeClick = useCallback(
    (hierarchyPointNode: HierarchyPointNode<TreeNodeDatum>) => {
      const datum = hierarchyPointNode.data;
      const kind = datum.attributes?.kind;
      const uid = datum.attributes?.userId as string | undefined;
      if (kind === 'user' && uid) {
        if (authUser?.id && uid === authUser.id) {
          navigate(PageName.profile);
        } else {
          navigate(PageName.editUser.replace('{0}', uid));
        }
      }
    },
    [navigate, authUser?.id],
  );

  // Not memoized: Node passes a fresh `onNodeClick` each render; stale closure would break navigation.
  const renderCustomNode = ({ nodeDatum, onNodeClick }: CustomNodeElementProps) => {
    const isCurrentUser = nodeDatum.attributes?.isCurrentUser === true;
    const isVirtualRoot = nodeDatum.attributes?.kind === 'virtual';
    const role = (nodeDatum.attributes?.role as string) || '';
    const roleKey = String(nodeDatum.attributes?.roleKey ?? '');
    const userId = String(nodeDatum.attributes?.userId ?? '');

    const fillColor = isVirtualRoot ? colors.grey : getFillColorForRole(roleKey);

    return (
      <g
        className='user-org-chart-node'
        data-user-id={userId}
        style={{ cursor: isVirtualRoot ? 'default' : 'pointer' }}
        onClick={onNodeClick}
      >
        {/* Highlight ring for current user (role color stays the same) */}
        {isCurrentUser && (
          <>
            <rect
              x={-NODE_W / 2 - 5}
              y={-NODE_H / 2 - 5}
              width={NODE_W + 10}
              height={NODE_H + 10}
              rx={11}
              ry={11}
              fill='none'
              stroke={CURRENT_USER_RING}
              strokeWidth={4}
              pointerEvents='none'
            />
          </>
        )}
        <rect
          x={-NODE_W / 2}
          y={-NODE_H / 2}
          width={NODE_W}
          height={NODE_H}
          rx={8}
          ry={8}
          fill={fillColor}
          stroke='none'
        />
        <foreignObject x={-NODE_W / 2} y={-NODE_H / 2} width={NODE_W} height={NODE_H}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              boxSizing: 'border-box',
              padding: '6px 10px',
              margin: 0,
              textAlign: 'center',
              fontFamily: ORG_CHART_FONT_FAMILY,
              color: '#ffffff',
              overflow: 'hidden',
              pointerEvents: 'none',
            }}
          >
            <span
              style={{
                display: 'block',
                fontWeight: 700,
                fontSize: 14,
                lineHeight: 1.2,
                color: '#ffffff',
                wordBreak: 'break-word',
              }}
            >
              {nodeDatum.name}
            </span>
            {role ? (
              <span
                style={{
                  display: 'block',
                  marginTop: 4,
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: 1.2,
                  color: '#e8eef8',
                  wordBreak: 'break-word',
                }}
              >
                {role}
              </span>
            ) : null}
          </div>
        </foreignObject>
      </g>
    );
  };

  if (!treeData) return null;

  const linkColor = isDarkMode ? 'rgba(165,182,200,0.5)' : 'rgba(45,81,118,0.4)';
  const linkConnectedColor = isDarkMode ? 'rgba(239,83,80,0.8)' : 'rgba(239,83,80,0.7)';
  const bgColor = isDarkMode ? colors.darkBackground2 : 'white';

  return (
    <>
      <Box
        borderRadius={1}
        sx={{
          boxShadow: 1,
          maxWidth: 750,
          overflow: 'hidden',
          mt: 3,
          ...(sx && typeof sx === 'object' && !Array.isArray(sx) ? sx : {}),
        }}
        bgcolor={bgColor}
        pb={2}
      >
        {/* Same header pattern as EditUser left card: title + trailing actions */}
        <Flex p={2}>
          <Box>
            <Body1 weight={600} size='1.1rem'>
              {ls.userHierarchy}
            </Body1>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 35, flexShrink: 0 }}>
            {currentUserId && (
              <Tooltip title={ls.orgChartCenterOnUser}>
                <IconButton
                  size='small'
                  aria-label={ls.orgChartCenterOnUser}
                  onClick={handleCenterOnCurrentUser}
                  sx={{
                    color: isDarkMode ? colors.lightGrey : colors.blue,
                  }}
                >
                  <MyLocationIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={ls.orgChartShowAll}>
              <IconButton
                size='small'
                aria-label={ls.orgChartShowAll}
                onClick={handleShowAll}
                sx={{
                  color: isDarkMode ? colors.lightGrey : colors.blue,
                }}
              >
                <FullscreenIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Flex>
        <Box className='separator' width='100%' />
        <Box m={2}>
          <Box
            ref={containerRef}
            sx={{
              width: '100%',
              height: 440,
              position: 'relative',
              '& .rd3t-link': {
                stroke: linkColor,
                strokeWidth: '1.5px',
              },
              '& .rd3t-link.link-connected-to-current-user': {
                stroke: linkConnectedColor,
                strokeWidth: '3px',
              },
              '& .rd3t-node circle, & .rd3t-leaf-node circle': {
                display: 'none',
              },
            }}
          >
            {ready && (
              <Tree
                ref={treeRef}
                key={viewResetKey}
                data={treeData}
                orientation='vertical'
                translate={translate}
                zoom={zoom}
                renderCustomNodeElement={renderCustomNode}
                onNodeClick={handleNodeClick}
                pathClassFunc={pathClassFunc}
                nodeSize={{ x: NODE_W + 40, y: NODE_H + 90 }}
                separation={{ siblings: 1.3, nonSiblings: 1.6 }}
                pathFunc='step'
                scaleExtent={{ min: 0.15, max: 2 }}
                initialDepth={Infinity}
                collapsible={false}
                enableLegacyTransitions={false}
              />
            )}
          </Box>
        </Box>
      </Box>

      {/* Fullscreen Dialog */}
      <Dialog
        open={isFullscreen}
        onClose={handleCloseFullscreen}
        maxWidth={false}
        sx={{
          zIndex: 9999,
          '& .MuiDialog-paper': {
            bgcolor: bgColor,
            width: '85vw',
            height: '93vh',
            maxWidth: '85vw',
            maxHeight: '93vh',
            margin: 'auto',
            borderRadius: 2,
            zIndex: 9999,
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9998,
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: bgColor,
          }}
        >
          <Flex p={2} sx={{ borderBottom: `1px solid ${isDarkMode ? '#333' : '#e0e0e0'}` }}>
            <Box>
              <Body1 weight={600} size='1.1rem'>
                {ls.userHierarchy}
              </Body1>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 35, flexShrink: 0 }}>
              <Tooltip title={ls.orgChartResetView}>
                <IconButton
                  size='small'
                  aria-label={ls.orgChartResetView}
                  onClick={handleResetFullscreenView}
                  sx={{
                    color: isDarkMode ? colors.lightGrey : colors.blue,
                  }}
                >
                  <FitScreenIcon />
                </IconButton>
              </Tooltip>
              <Tooltip
                title={
                  // @ts-ignore
                  ls.close
                }
              >
                <IconButton
                  size='small'
                  aria-label={
                    // @ts-ignore
                    ls.close
                  }
                  onClick={handleCloseFullscreen}
                  sx={{
                    color: isDarkMode ? colors.lightGrey : colors.blue,
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Flex>
          <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            <Box
              ref={fullscreenContainerRef}
              sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
                '& .rd3t-link': {
                  stroke: linkColor,
                  strokeWidth: '1.5px',
                },
                '& .rd3t-link.link-connected-to-current-user': {
                  stroke: linkConnectedColor,
                  strokeWidth: '3px',
                },
                '& .rd3t-node circle, & .rd3t-leaf-node circle': {
                  display: 'none',
                },
              }}
            >
            {isFullscreen && treeData && (
              <Tree
                key={fullscreenResetKey}
                data={treeData}
                orientation='vertical'
                translate={fullscreenTranslate}
                zoom={fullscreenZoom}
                renderCustomNodeElement={renderCustomNode}
                onNodeClick={handleNodeClick}
                pathClassFunc={pathClassFunc}
                nodeSize={{ x: NODE_W + 40, y: NODE_H + 90 }}
                separation={{ siblings: 1.3, nonSiblings: 1.6 }}
                pathFunc='step'
                scaleExtent={{ min: 0.1, max: 2.5 }}
                initialDepth={Infinity}
                collapsible={false}
                enableLegacyTransitions={false}
              />
            )}
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default UserOrgChart;
